// frontend/src/components/Seo.js
//
// The per-URL half of the SEO story. index.html carries what is true of every
// page — the title, the description, the Open Graph image — and this keeps the
// three tags that must change as you navigate in step with the URL:
//
//   <link rel="canonical">        which URL this page really is
//   <link rel="alternate" hreflang>  the same page in the other language
//   <meta name="robots">          whether it should be indexed at all
//
// Written directly to document.head rather than through react-helmet: three
// tags do not justify a dependency, and Googlebot reads the DOM after the app
// has rendered, so the tags being placed by React is not a problem.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LANGUAGES, DEFAULT_LANGUAGE, getLanguage } from '../i18n';
import { stripLocale } from '../routing';
import { SITE_ORIGIN } from '../config';

// Paths that must never reach an index — someone's rentals, someone's profile,
// the operator's console, and the one-shot token pages whose URLs are secrets.
// Matched against the path with the locale prefix already removed.
const NOINDEX = [
  '/my-rentals', '/profile', '/rental', '/checkout', '/admin',
  '/confirm-email', '/verify-email', '/reset-password', '/delete-account',
  '/styleguide',
];

export const isIndexable = (path) => !NOINDEX.some((p) => path === p || path.startsWith(p + '/'));

// og:locale wants a territory ('el_CY'), hreflang wants a language tag ('el').
// Both come off the registry so a third language needs no edit here.
const ogLocale = (lng) => (LANGUAGES[lng]?.intl || lng).replace('-', '_');

function upsert(selector, tag, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export default function Seo() {
  const { pathname } = useLocation();
  const lang = getLanguage();

  useEffect(() => {
    const path = stripLocale(pathname);
    const clean = path === '/' ? '' : path;
    const url = (lng) => `${SITE_ORIGIN}/${lng}${clean}`;
    const indexable = isIndexable(path);

    upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: url(lang) });
    upsert('meta[property="og:url"]', 'meta', { property: 'og:url', content: url(lang) });
    upsert('meta[property="og:locale"]', 'meta', { property: 'og:locale', content: ogLocale(lang) });

    // hreflang is reciprocal or it is ignored: every version must list every
    // version including itself. x-default names the one to serve a reader whose
    // language we do not publish.
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    if (indexable) {
      Object.keys(LANGUAGES).forEach((lng) => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', LANGUAGES[lng].htmlLang);
        el.setAttribute('href', url(lng));
        document.head.appendChild(el);
      });
      const dflt = document.createElement('link');
      dflt.setAttribute('rel', 'alternate');
      dflt.setAttribute('hreflang', 'x-default');
      dflt.setAttribute('href', url(DEFAULT_LANGUAGE));
      document.head.appendChild(dflt);
    }

    // robots.txt already disallows these paths, but a URL that is linked from
    // elsewhere can still be indexed without being crawled. The meta tag is
    // what actually keeps it out, and it is only honoured if the crawler is
    // allowed to fetch the page — hence both.
    const robots = document.head.querySelector('meta[name="robots"]');
    if (indexable) {
      if (robots) robots.remove();
    } else {
      upsert('meta[name="robots"]', 'meta', { name: 'robots', content: 'noindex, nofollow' });
    }
  }, [pathname, lang]);

  return null;
}
